import { Form, Input } from 'antd';
import { CurrencyBadge } from '@/components/currency-badge';
import { cn } from '@/libs/utils';
import { getFieldState } from '@/utils/formUtils';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

// Allowances an employee may have no amount for.
const OPTIONAL_ALLOWANCES = ['workFromHomeAllowance'];

/**
 * One allowance: its name with how it is paid underneath, the amount and the
 * currency on the right. While viewing the amount is plain text; the field
 * stays mounted (only hidden) so the form never sees it as missing.
 */
function AmountRow({ row, form, formFields, isEdited, readOnly, t }) {
  const name = row.groupName ? [row.groupName, row.inputName] : row.inputName;
  const state = getFieldState({ formFields, formKeys: name, isEdited });
  const value = Form.useWatch(name, { form, preserve: true });
  const filled = value !== undefined && value !== null && value !== '';
  const method = [row.unitPart, row.valuePart].filter(Boolean).join(' · ');

  return (
    <div className="flex items-center gap-3 border-t border-zinc-100 px-3 py-2 first:border-t-0">
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-[12.5px] font-semibold text-zinc-800">
          {row.inputLabel}
        </span>
        {method && (
          <span className="block text-[11.5px] text-zinc-500">{method}</span>
        )}
      </span>
      <div
        className={cn(
          // Same alignment rules as the annual-leave balance: the figure ends
          // at the same edge in both the view and edit modes.
          'w-28 shrink-0 text-right text-[15px] font-bold [&_.ant-form-item-explain]:text-left [&_.ant-form-item-explain]:text-[11px] [&_input.ant-input]:!pr-[7px] [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:text-right [&_input]:[-moz-appearance:textfield]',
          readOnly && 'pr-2',
          readOnly && !Number(value) ? 'text-zinc-400' : 'text-zinc-900'
        )}
      >
        {readOnly && (filled ? Number(value).toLocaleString('sr-RS') : '—')}
        <Form.Item
          name={name}
          className="!mb-0"
          hidden={readOnly}
          rules={[
            {
              // Work from home is paid per day worked from home: someone who
              // never does has no amount, and that must not block saving the
              // tile (it did, until a 0 was typed in).
              required: !OPTIONAL_ALLOWANCES.includes(row.inputName),
              message: t('formValidation.numberField.invalid', {
                ns: 'forms',
              }),
            },
          ]}
        >
          <Input
            min={0}
            type="number"
            step="any"
            size="small"
            placeholder="—"
            onChange={(e) => formValidateAndSetNumber(form, e, name, 0)}
            disabled={state?.disabled}
          />
        </Form.Item>
      </div>
      <span className="flex w-[74px] shrink-0">
        {row.currency?.currencyCode ? (
          <CurrencyBadge
            className="mt-0 h-6"
            currency={row.currency.label}
            currencyCode={row.currency.currencyCode}
          />
        ) : (
          <span className="pl-2 text-zinc-400">—</span>
        )}
      </span>
    </div>
  );
}

function AmountGroup({ title, rows, ...rest }) {
  if (!rows?.length) return null;
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
        {title}
      </h3>
      <div className="rounded-xl border border-zinc-200 bg-white">
        {rows.map((row) => (
          <AmountRow key={row.inputName} row={row} {...rest} />
        ))}
      </div>
    </section>
  );
}

/**
 * The "Earnings" tile of the employee profile: the allowances the company
 * pays (meal, holiday, travel, work from home) and the other income and
 * deductions, each as an amount with its currency. Same fields, rules and
 * save as the tables of EmployeeAllowancesFields, which step aside on the
 * profile.
 */
export default function EmployeeAllowancesProfilePanel() {
  const { COMPANY_DATA, form, formFields, isEdited, readOnly, t } =
    useEmployeeGeneralDataForm();
  const tp = (key) =>
    t(`employeeProfile.earningsUi.${key}`, { ns: 'translations' });
  const rowProps = { form, formFields, isEdited, readOnly, t };

  return (
    <div className="grid max-w-[560px] gap-4">
      <AmountGroup
        title={tp('groupAllowances')}
        rows={COMPANY_DATA.calculationMethods}
        {...rowProps}
      />
      <AmountGroup
        title={tp('groupOther')}
        rows={COMPANY_DATA.otherAllowancesData}
        {...rowProps}
      />
    </div>
  );
}
